/**
 * Audio Compression Utilities
 * Handles WAV compression with multiple quality levels
 */

export interface CompressionSettings {
  bitDepth: number;
  sampleRate: number;
  compressionLevel: number;
}

export type CompressionLevel = 1 | 2 | 3 | 4;
export type Mp3QualityLevel = 96 | 128 | 192 | 320;

/**
 * Get compression settings based on quality level
 */
export const getCompressionSettings = (
  compressionLevel: CompressionLevel, 
  originalSampleRate: number = 44100
): CompressionSettings => {
  switch (compressionLevel) {
    case 4: // Highest compression (MP3 96kbps equivalent)
      return {
        bitDepth: 8,
        sampleRate: Math.min(originalSampleRate, 22050),
        compressionLevel: 4
      };
    case 3: // High compression (MP3 128kbps equivalent)  
      return {
        bitDepth: 8,
        sampleRate: Math.min(originalSampleRate, 32000),
        compressionLevel: 3
      };
    case 2: // Medium compression (MP3 192kbps equivalent)
      return {
        bitDepth: 12,
        sampleRate: Math.min(originalSampleRate, 44100),
        compressionLevel: 2
      };
    case 1: // Light compression (MP3 320kbps equivalent)
      return {
        bitDepth: 14,
        sampleRate: originalSampleRate,
        compressionLevel: 1
      };
    default:
      return {
        bitDepth: 8,
        sampleRate: Math.min(originalSampleRate, 22050),
        compressionLevel: 4
      };
  }
};

/**
 * Map MP3 quality levels to compression levels
 */
export const mp3QualityToCompressionLevel = (mp3Quality: Mp3QualityLevel): CompressionLevel => {
  switch (mp3Quality) {
    case 96: return 4;
    case 128: return 3;
    case 192: return 2;
    case 320: return 1;
    default: return 3;
  }
};

/**
 * Calculate estimated file size for compressed audio
 */
export const calculateCompressedFileSize = (
  duration: number,
  compressionLevel: CompressionLevel,
  channels: number = 2,
  originalSampleRate: number = 44100
): number => {
  const settings = getCompressionSettings(compressionLevel, originalSampleRate);
  const bytesPerSample = Math.ceil(settings.bitDepth / 8);
  const totalBytes = duration * settings.sampleRate * channels * bytesPerSample + 44; // +44 for WAV header
  return Math.round(totalBytes / (1024 * 1024)); // Convert to MB
};

/**
 * Calculate standard WAV file size
 */
export const calculateStandardWavFileSize = (
  duration: number,
  compress: boolean = false,
  channels: number = 2,
  sampleRate: number = 44100
): number => {
  const bitDepth = compress ? 8 : 16;
  const bytesPerSample = bitDepth / 8;
  const totalBytes = duration * sampleRate * channels * bytesPerSample + 44;
  return Math.round(totalBytes / (1024 * 1024));
};

/**
 * Get quality description for compression level
 */
export const getCompressionLevelDescription = (level: CompressionLevel): string => {
  switch (level) {
    case 4: return 'Highest compression (~25MB), 8-bit/22kHz - Good for speech';
    case 3: return 'High compression (~35MB), 8-bit/32kHz - Recommended';  
    case 2: return 'Medium compression (~55MB), 12-bit/44kHz - Great for music';
    case 1: return 'Light compression (~85MB), 14-bit/44kHz - Premium quality';
    default: return 'High compression - Recommended';
  }
};

/**
 * Get quality name for compression level
 */
export const getCompressionLevelName = (level: CompressionLevel): string => {
  switch (level) {
    case 4: return 'Good';
    case 3: return 'High';
    case 2: return 'Very High';
    case 1: return 'Maximum';
    default: return 'High';
  }
};
